import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Plus, Trash2, Edit2, Save, X, Image as ImageIcon, Calendar, Clock, User, Link as LinkIcon, Loader, RefreshCw, Upload, Filter, Search, Download } from 'lucide-react';
import { triggerAutoUpdate } from '../../utils/sitemapService';

// ... (existing code)


const WebinarManager = () => {
    const [events, setEvents] = useState([]);
    const [attendees, setAttendees] = useState([]);
    const [activeTab, setActiveTab] = useState('events'); // 'events' or 'attendees'
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Filters
    const [filterEvent, setFilterEvent] = useState('');
    const [filterDate, setFilterDate] = useState('');

    // ... (existing state and functions)

    useEffect(() => {
        if (activeTab === 'events') {
            fetchEvents();
        } else {
            fetchAttendees();
        }
    }, [activeTab]);

    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            const q = query(collection(db, 'events'), orderBy('date', 'asc'));
            const querySnapshot = await getDocs(q);
            const eventsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setEvents(eventsData);
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAttendees = async () => {
        setIsLoading(true);
        try {
            let q = collection(db, 'attendees');
            // Note: simple filtering client-side for now as compound queries need indexes
            const querySnapshot = await getDocs(q);
            const attendeesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAttendees(attendeesData);
        } catch (error) {
            console.error("Error fetching attendees:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Updated Seed Data for March-June 2026
    const seedData = async () => {
        if (!window.confirm("Add sample 2026 events (March-June)?")) return;
        setIsLoading(true);
        try {
            const sampleEvents = [
                {
                    title: "Strategic Business Development Training",
                    date: "2026-03-10",
                    time: "09:00 AM EST",
                    description: "Master the art of BPO sales and pipeline management. A comprehensive workshop for BD professionals.",
                    category: "training",
                    registration_link: "",
                    image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=1000",
                    speakers: [{ name: "Alex Mercer", role: "Sales Director", image: "" }],
                    createdAt: new Date().toISOString()
                },
                {
                    title: "BPO Leaders Meet & Greet: Spring Mixer",
                    date: "2026-04-15",
                    time: "05:00 PM EST",
                    description: "Networking event for industry leaders to exchange ideas and discuss market trends.",
                    category: "networking",
                    registration_link: "",
                    image: "https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&q=80&w=1000",
                    speakers: [],
                    createdAt: new Date().toISOString()
                },
                {
                    title: "Advanced Negotiation Strategies",
                    date: "2026-05-20",
                    time: "02:00 PM EST",
                    description: "Negotiation tactics for high-value BPO contracts. Interactive role-playing session.",
                    category: "training",
                    registration_link: "",
                    image: "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&q=80&w=1000",
                    speakers: [{ name: "Sarah Jenkins", role: "Contract Specialist", image: "" }],
                    createdAt: new Date().toISOString()
                },
                {
                    title: "Summer Innovation Summit",
                    date: "2026-06-12",
                    time: "10:00 AM EST",
                    description: "Showcasing the latest tools and technologies driving efficiency in outsourcing.",
                    category: "conference",
                    registration_link: "",
                    image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=1000",
                    speakers: [{ name: "Dr. Arinze", role: "Tech Innovator", image: "" }],
                    createdAt: new Date().toISOString()
                }
            ];

            for (const event of sampleEvents) {
                await addDoc(collection(db, 'events'), event);
            }
            fetchEvents();
            alert("Sample 2026 events added!");
        } catch (error) {
            console.error("Error seeding data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Initial state for a new event
    const initialEventState = {
        title: '',
        date: '',
        time: '',
        description: '',
        image: '', // URL
        registration_link: '',
        notificationEmail: '', // New field for attendee notifications
        speakers: [] // { name, role, image }
    };

    const [formData, setFormData] = useState(initialEventState);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e, field = 'image', speakerIndex = null) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const storageRef = ref(storage, `events/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            if (speakerIndex !== null) {
                const updatedSpeakers = [...formData.speakers];
                updatedSpeakers[speakerIndex].image = downloadURL;
                setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
            } else {
                setFormData(prev => ({ ...prev, [field]: downloadURL }));
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Image upload failed. Please ensure Firebase Storage is enabled or use a URL.");
        } finally {
            setUploading(false);
        }
    };

    const handleAddSpeaker = () => {
        setFormData(prev => ({
            ...prev,
            speakers: [...prev.speakers, { name: '', role: '', image: '' }]
        }));
    };

    const handleRemoveSpeaker = (index) => {
        const updatedSpeakers = formData.speakers.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
    };

    const handleSpeakerChange = (index, field, value) => {
        const updatedSpeakers = [...formData.speakers];
        updatedSpeakers[index][field] = value;
        setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (currentEvent) {
                await updateDoc(doc(db, 'events', currentEvent.id), formData);
            } else {
                await addDoc(collection(db, 'events'), {
                    ...formData,
                    category: 'webinar', // Default category
                    createdAt: new Date().toISOString()
                });
            }
            setIsEditing(false);
            setCurrentEvent(null);
            setFormData(initialEventState);
            fetchEvents();

            // Trigger Sitemap Update
            await triggerAutoUpdate();
        } catch (error) {
            console.error("Error saving event:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (event) => {
        setCurrentEvent(event);
        setFormData(event);
        setIsEditing(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this event?")) {
            try {
                await deleteDoc(doc(db, 'events', id));
                fetchEvents();

                // Trigger Sitemap Update
                await triggerAutoUpdate();
            } catch (error) {
                console.error("Error deleting event:", error);
            }
        }
    };

    const openNewModal = () => {
        setCurrentEvent(null);
        setFormData(initialEventState);
        setIsEditing(true);
    };

    // Filter Logic
    const filteredAttendees = attendees.filter(att => {
        const matchEvent = filterEvent ? att.eventTitle.toLowerCase().includes(filterEvent.toLowerCase()) : true;
        const matchDate = filterDate ? att.eventDate === filterDate : true;
        return matchEvent && matchDate;
    });

    const handleExportCSV = () => {
        if (filteredAttendees.length === 0) {
            alert("No attendees to export.");
            return;
        }

        const headers = ["Name", "Email", "Company", "Job Title", "Event", "Event Date", "Registered At"];
        const csvRows = [headers.join(',')];

        filteredAttendees.forEach(att => {
            const row = [
                `"${att.name}"`,
                `"${att.email}"`,
                `"${att.company}"`,
                `"${att.jobTitle}"`,
                `"${att.eventTitle}"`,
                `"${att.eventDate}"`,
                `"${new Date(att.registeredAt).toISOString()}"`
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `webinar_attendees_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Content</div>
                    <h1>Webinars & Events <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <Link to="/admin/dashboard" className="btn btn-ghost" style={{ borderColor: 'var(--border)', color: 'var(--text2)', marginRight: '8px' }}>
                        Dashboard
                    </Link>
                    <button className="btn btn-ghost" onClick={seedData}>
                        <RefreshCw size={14} /> Seed Data
                    </button>
                    {activeTab === 'events' && (
                        <button className="btn btn-primary" onClick={openNewModal}>
                            <Plus size={14} /> Add Event
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[var(--border)] mb-6">
                <button
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'events' ? 'border-[var(--accent)] text-[var(--text)]' : 'border-transparent text-[var(--muted)] hover:text-[var(--text)]'}`}
                    onClick={() => setActiveTab('events')}
                >
                    Events List
                </button>
                <button
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'attendees' ? 'border-[var(--accent)] text-[var(--text)]' : 'border-transparent text-[var(--muted)] hover:text-[var(--text)]'}`}
                    onClick={() => setActiveTab('attendees')}
                >
                    Attendees
                </button>
            </div>

            {/* Events Tab Content */}
            {activeTab === 'events' && (
                !isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* ... Existing Event Grid ... */}
                        {isLoading ? (
                            <div className="col-span-full text-center py-10 text-[var(--muted)]">Loading events...</div>
                        ) : events.length === 0 ? (
                            <div className="col-span-full text-center py-10 text-[var(--muted)] border-2 border-dashed border-[var(--border)] rounded-xl">
                                No events found. Add one to get started.
                            </div>
                        ) : (
                            events.map(event => (
                                <div key={event.id} className="card overflow-hidden hover:shadow-lg transition-all group">
                                    <div className="h-40 bg-gray-800 relative overflow-hidden">
                                        {event.image ? (
                                            <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-[var(--muted)]">
                                                <ImageIcon size={32} />
                                            </div>
                                        )}
                                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => handleEdit(event)} className="p-2 bg-[var(--surface2)] rounded-lg shadow-sm hover:bg-[var(--surface)] text-blue-400">
                                                <Edit2 size={14} />
                                            </button>
                                            <button onClick={() => handleDelete(event.id)} className="p-2 bg-[var(--surface2)] rounded-lg shadow-sm hover:bg-[var(--surface)] text-[var(--accent3)]">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center gap-2 text-xs font-medium text-[var(--muted)] mb-2">
                                            <span className="flex items-center gap-1"><Calendar size={12} /> {event.date}</span>
                                            <span className="flex items-center gap-1"><Clock size={12} /> {event.time}</span>
                                        </div>
                                        <h3 className="font-bold text-[var(--text)] mb-2 line-clamp-1" title={event.title}>{event.title}</h3>
                                        <p className="text-sm text-[var(--text2)] line-clamp-2 mb-4">{event.description}</p>
                                        <div className="flex items-center -space-x-2">
                                            {event.speakers && event.speakers.map((s, i) => (
                                                <div key={i} className="w-6 h-6 rounded-full bg-[var(--surface)] border border-[var(--border)] overflow-hidden" title={s.name}>
                                                    {s.image ? <img src={s.image} alt={s.name} className="w-full h-full object-cover" /> : <User size={12} className="w-full h-full p-1 text-[var(--muted)]" />}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    // ... Existing Edit Form ...
                    <div className="max-w-3xl mx-auto card p-8 shadow-sm">
                        {/* ... form content same as before ... */}
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-[var(--text)]">{currentEvent ? 'Edit Event' : 'New Event'}</h2>
                            <button onClick={() => setIsEditing(false)} className="btn btn-ghost"><X size={16} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* ... fields ... */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[var(--muted)] mb-1">Title</label>
                                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="form-input w-full p-2" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[var(--muted)] mb-1">Date</label>
                                    <input type="date" name="date" value={formData.date} onChange={handleInputChange} className="form-input w-full p-2" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[var(--muted)] mb-1">Time</label>
                                    <input type="text" name="time" value={formData.time} onChange={handleInputChange} placeholder="e.g. 10:00 AM EST" className="form-input w-full p-2" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[var(--muted)] mb-1">Registration Link</label>
                                    <div className="flex bg-[var(--surface2)] border border-[var(--border)] rounded-lg overflow-hidden">
                                        <div className="p-2 bg-[var(--surface3)] border-r border-[var(--border)]"><LinkIcon size={16} className="text-[var(--muted)]" /></div>
                                        <input type="text" name="registration_link" value={formData.registration_link} onChange={handleInputChange} placeholder="https://..." className="w-full p-2 bg-transparent text-[var(--text)] focus:outline-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[var(--muted)] mb-1">Attendee Notification Email</label>
                                    <input
                                        type="email"
                                        name="notificationEmail"
                                        value={formData.notificationEmail || ''}
                                        onChange={handleInputChange}
                                        placeholder="leads@event-specific.com"
                                        className="form-input w-full p-2"
                                    />
                                    <p className="text-xs text-[var(--muted)] mt-1">Leave blank to use global default.</p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[var(--muted)] mb-1">Description</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3" className="form-textarea w-full p-2" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[var(--muted)] mb-1">Event Banner Image</label>
                                <div className="flex items-center gap-4">
                                    {formData.image && <img src={formData.image} alt="Preview" className="w-20 h-12 object-cover rounded border border-[var(--border)]" />}
                                    <label className="cursor-pointer btn btn-ghost btn-sm flex items-center gap-2">
                                        {uploading ? <Loader className="animate-spin" size={14} /> : <Upload size={14} />}
                                        Upload Image
                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e)} disabled={uploading} />
                                    </label>
                                    <span className="text-xs text-[var(--muted)]">or URL:</span>
                                    <input type="text" name="image" value={formData.image} onChange={handleInputChange} placeholder="Image URL" className="flex-1 form-input p-2 text-sm" />
                                </div>
                            </div>
                            {/* Speakers */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-sm font-medium text-[var(--muted)]">Speakers</label>
                                    <button type="button" onClick={handleAddSpeaker} className="text-xs text-[var(--accent)] font-bold flex items-center gap-1"><Plus size={12} /> Add Speaker</button>
                                </div>
                                <div className="space-y-3">
                                    {formData.speakers.map((speaker, index) => (
                                        <div key={index} className="flex flex-col md:flex-row gap-3 p-3 bg-[var(--surface2)] rounded-lg border border-[var(--border)]">
                                            <div className="flex-1">
                                                <input type="text" placeholder="Name" value={speaker.name} onChange={(e) => handleSpeakerChange(index, 'name', e.target.value)} className="form-input w-full p-2 text-sm mb-2 md:mb-0" />
                                                <input type="text" placeholder="Role" value={speaker.role} onChange={(e) => handleSpeakerChange(index, 'role', e.target.value)} className="form-input w-full p-2 text-sm" />
                                            </div>
                                            <div className="flex flex-col gap-2 justify-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-10 h-10 bg-[var(--surface3)] rounded-full overflow-hidden flex-shrink-0">
                                                        {speaker.image ? <img src={speaker.image} alt="" className="w-full h-full object-cover" /> : <User size={20} className="m-auto mt-2 text-[var(--muted)]" />}
                                                    </div>
                                                    <label className="cursor-pointer text-xs bg-[var(--surface)] border border-[var(--border)] px-2 py-1 rounded hover:bg-[var(--surface3)] text-[var(--text)]">Upload <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'image', index)} disabled={uploading} /></label>
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => handleRemoveSpeaker(index)} className="text-[var(--accent3)] hover:text-red-500 self-center p-2"><Trash2 size={16} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
                                <button type="button" onClick={() => setIsEditing(false)} className="btn btn-ghost">Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isLoading || uploading}>{isLoading ? <Loader className="animate-spin" size={14} /> : <Save size={14} />} {currentEvent ? 'Update Event' : 'Create Event'}</button>
                            </div>
                        </form>
                    </div>
                )
            )}

            {/* Attendees Tab Content */}
            {activeTab === 'attendees' && (
                <div className="card">
                    {/* Filters */}
                    <div className="card-hdr flex flex-wrap gap-4 items-center !p-4">
                        <div className="flex items-center text-sm font-bold text-[var(--muted)] gap-2"><Filter size={14} /> Filters:</div>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                            <input
                                type="text"
                                placeholder="Filter by Event Name..."
                                value={filterEvent}
                                onChange={(e) => setFilterEvent(e.target.value)}
                                className="search-input w-full md:w-64"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="form-input p-2"
                            />
                            {filterDate && <button onClick={() => setFilterDate('')} className="text-xs text-[var(--accent3)] hover:underline">Clear Date</button>}
                        </div>
                        <div className="flex-1"></div>
                        <button onClick={handleExportCSV} className="btn btn-outline btn-sm flex items-center gap-2">
                            <Download size={14} /> Export CSV
                        </button>
                    </div>

                    <div className="tbl-scroll">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="text-[var(--text2)]">Name</th>
                                    <th className="text-[var(--text2)]">Email</th>
                                    <th className="text-[var(--text2)]">Company</th>
                                    <th className="text-[var(--text2)]">Job Title</th>
                                    <th className="text-[var(--text2)]">Event</th>
                                    <th className="text-[var(--text2)]">Event Date</th>
                                    <th className="text-[var(--text2)]">Registered At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="7" className="p-8 text-center text-[var(--muted)]">Loading attendees...</td></tr>
                                ) : filteredAttendees.length === 0 ? (
                                    <tr><td colSpan="7" className="p-8 text-center text-[var(--muted)]">No attendees found matching filters.</td></tr>
                                ) : (
                                    filteredAttendees.map(att => (
                                        <tr key={att.id} className="hover:bg-[var(--surface3)] transition-colors">
                                            <td className="text-white font-medium">{att.name}</td>
                                            <td className="text-[var(--text)]">{att.email}</td>
                                            <td className="text-[var(--text2)]">{att.company}</td>
                                            <td className="text-[var(--text2)]">{att.jobTitle}</td>
                                            <td style={{ color: 'var(--accent)' }}>{att.eventTitle}</td>
                                            <td className="text-[var(--text2)]">{att.eventDate}</td>
                                            <td className="text-[var(--muted)] text-xs">{new Date(att.registeredAt).toLocaleDateString()}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-3 border-t border-[var(--border)] text-xs text-[var(--muted)] text-right">
                        Total Attendees: {filteredAttendees.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebinarManager;
